<script lang="ts">
  import {createEventDispatcher} from 'svelte';
  import InlineSVG from 'svelte-inline-svg';
  import Cropper from 'cropperjs';
  import CropperCSS from './CropperCSS.svelte';

  import desktopIcon from '../../assets/icons/desktop.svg';
  import smartphoneIcon from '../../assets/icons/smartphone.svg';

  import ImageEditor from '../image-editor/ImageEditor.svelte';
  import LoadingSpinner from '../loader/LoadingSpinner.svelte';
  import Button from '../button/Button.svelte';

  const dispatch = createEventDispatcher();

  export let imageUrl = '';
  export let imageName = '';

  const aspectRatio = {
    desktop: 16 / 9,
    smartphone: 16 / 27 // should be 1/3 of the desktop size
  };

  let isLoading = true;
  let canvasElement: HTMLCanvasElement;
  let cropper: Cropper;
  let device: 'desktop' | 'smartphone' = 'desktop';

  const cropperOptions: Cropper.Options<HTMLCanvasElement> = {
    aspectRatio: aspectRatio[device],
    viewMode: 1,
    preview: '.imagePreview',
    ready: () => (isLoading = false)
  };

  // Open image in cropper
  $: {
    if (canvasElement && imageUrl) {
      const context = canvasElement.getContext('2d');
      const image = new Image();

      image.onload = function () {
        if (context) {
          context.canvas.height = image.height;
          context.canvas.width = image.width;
          context.drawImage(image, 0, 0);
          cropper = new Cropper(canvasElement, cropperOptions);
        }
      };

      image.src = imageUrl;
    }
  }

  const onChangeDevice: svelte.JSX.FormEventHandler<
    HTMLInputElement
  > = event => {
    if (cropper) {
      const device = event.currentTarget.value;
      if (device === 'desktop' || device === 'smartphone') {
        cropper.setAspectRatio(aspectRatio[device]);
      }
    }
  };
</script>

<style lang="postcss">
  .loadingContainer {
    position: absolute;
    top: 0;
    left: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
  }

  .canvasWrapper {
    padding-bottom: calc(100% / 16 * 9); /* aspect ratio 16:9 */
    border: 1px solid var(--ubilabs-gray);
    background-color: var(--ubilabs-gray);
  }

  .canvasWrapper > :global(div) {
    position: absolute;
    top: 0;
    left: 0;
  }

  canvas {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }

  .deviceWrapper {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    padding: 0.5rem;
    text-align: center;
    pointer-events: none;
  }

  .deviceWrapper > label {
    position: relative;
    display: inline-flex;
    width: calc(var(--ubilabs-icon-button-icon-size) * 2);
    height: calc(var(--ubilabs-icon-button-icon-size) * 2);
    padding: calc(var(--ubilabs-icon-button-icon-size) / 2);
    background: none;
    outline: none;
    border: none;
    border-radius: var(--ubilabs-icon-button-icon-size);
    color: var(--ubilabs-white);
    font-size: var(--ubilabs-icon-button-icon-size);
    line-height: var(--ubilabs-icon-button-line-height);
    opacity: 0.6;
    pointer-events: all;
  }

  .deviceWrapper > label:hover,
  .deviceWrapper > label:focus-visible {
    opacity: 0.8;
  }

  .deviceWrapper > label.active {
    opacity: 1;
  }

  .deviceWrapper > label::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border-radius: var(--ubilabs-icon-button-icon-size);
    background-color: var(--ubilabs-gray-dark-2);
    opacity: 0.4;
  }

  .deviceWrapper > label:hover::before,
  .deviceWrapper > label:focus::before,
  .deviceWrapper > label.active::before {
    background-color: var(--ubilabs-gray-dark-1);
    opacity: 1;
  }

  .deviceWrapper > label:active::before {
    opacity: 0.6;
  }

  .deviceWrapper > label :global(svg) {
    z-index: 1;
  }

  .imagePreviewWrapper {
    max-width: 10rem;
    max-height: 10rem;
    margin-bottom: 0.5rem;
  }

  .imagePreview {
    padding-bottom: calc(100% / 16 * 9); /* aspect ratio 16:9 */
    overflow: hidden;
    border: 1px solid var(--ubilabs-gray);
    background-color: var(--ubilabs-gray);
  }
</style>

<CropperCSS />

<ImageEditor on:close let:closeEditor>
  <div slot="content">
    <div class="canvasWrapper">
      {#if isLoading}
        <div class="loadingContainer"><LoadingSpinner /></div>
      {/if}
      <canvas bind:this={canvasElement} />

      <div class="deviceWrapper">
        <label class:active={device === 'desktop'} for="device-desktop">
          <InlineSVG src={desktopIcon} />
        </label>
        <input
          type="radio"
          id="device-desktop"
          name="device"
          value="desktop"
          on:change={onChangeDevice}
          bind:group={device}
          hidden />

        <label class:active={device === 'smartphone'} for="device-smartphone">
          <InlineSVG src={smartphoneIcon} />
        </label>
        <input
          type="radio"
          id="device-smartphone"
          name="device"
          value="smartphone"
          on:change={onChangeDevice}
          bind:group={device}
          hidden />
      </div>
    </div>
  </div>

  <div slot="actions">
    <div class="imagePreviewWrapper">
      <div class="imagePreview" />
    </div>

    <Button
      disabled={!cropper}
      on:click={() => {
        if (cropper) {
          const croppedImageDataUrl = cropper
            .getCroppedCanvas()
            .toDataURL('image/jpeg');
          const {width, height} = cropper.getData();

          dispatch('crop', {
            name: imageName,
            dataUrl: croppedImageDataUrl,
            width,
            height
          });

          closeEditor();
        }
      }}>Crop image</Button>
  </div>
</ImageEditor>
